/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import Scene from "js_interface/src/scene_interface";
import {vec2} from "gl-matrix";
import WebGLContext from "App/web_gl_context";
import Messenger from "App/messenger";
import Canvas2DContext from "App/canvas_2d_context";
import CameraAnimator from "App/animations/camera_animator";
import AnimatedObject from "App/animations/animated_object";
import * as Debug from "App/debug";
/**
 * Provides requestAnimationFrame in a cross browser way.
 * https://cvs.khronos.org/svn/repos/registry/trunk/public/webgl/sdk/demos/common/webgl-utils.js
 */
window.requestAnimationFrame = (function() {
    // @ts-ignore
    return window.requestAnimationFrame ||
        window.webkitRequestAnimationFrame ||
        // @ts-ignore
        window.mozRequestAnimationFrame ||
        // @ts-ignore
        window.oRequestAnimationFrame ||
        // @ts-ignore
        window.msRequestAnimationFrame ||
        function(callback) { window.setTimeout(callback, 32); };
})();

export class HtmlCanvasExt {
    public dragging: boolean = false;
    public mouseButton: number = 0;
    public dragX: number = -1;
    public dragY: number = -1;
    public downX: number = 0;
    public downY: number = 0;
    public upX: number = 0;
    public upY: number = 0;
    public cdwkMouseWheelDelta: number = 0;
    public cdwkMouseForwarding: boolean = false;

    public cdwkEventForwarder: (aEvent: MouseEvent) => void;
}

/**
 * @param {SceneAPI.Scene} aSceneAPI
 * @param {*} aGlCanvasParentTag
 * @constructor
 */
export default
class Canvas {

    private static DoubleTapLimit: number = 500;          // Double tap time resolution in ms.
    private static LongTapLimit: number = 800;
    private static SingleDoubleClickLimit: number = 200;  // time in ms to wait for another click to differ click from double click
    private static SingleDoubleTapLimit: number = 200;
    private static SingleTapRadius: number = 5;
    private static DoubleTapRadius: number = 20;

    private _sceneAPI: Scene;
    private _glCanvasParentTag: HTMLDivElement;
    private _contentSize: vec2;
    private prevSquareDistance: number;
    private _minimalUIVisible: boolean;
    private _webglContext: WebGLContext;
    private _canvas2DContext: Canvas2DContext;
    private animatedObjects: AnimatedObject[];
    private mozillaIds: number[];
    private mozillaTouches: { touches: any[]; preventDefault: () => void };
    private _animationFrameRequestID: number | null;
    private _animationFrameContextObject: Window | VRDisplay;
    private _newRequestedAnimatioFrameOnObject: Window | VRDisplay;
    private _vrDeviceChecker: { checkRequested: boolean; displaysCount: number };
    private _dblClickTimer: number;
    private _clickHandled: boolean;
    private _touchLastTapStart: number;
    private _touchLastTapEnd: number;
    private _touchDoubleTapTimeout: number;
    private _touchLongTapTimeout: number;
    private _motionData: {
        initialScreenOffset: string | number,
        initialRoll: number,
        screenOrientation: string | number,
        deviceOrientation: {
            alpha: number,
            beta: number,
            gamma: number
        },
        changed: boolean
    };
    private _mainAnimationLoop: () => void;
    private _pgc: { pysicalDim: vec2; cssDim: vec2 };
    private htmlCanvas: HTMLCanvasElement;
    private htmlCanvasExt: HtmlCanvasExt = new HtmlCanvasExt();
    private html2DOverlayCanvas: HTMLCanvasElement;
    private _animationFrameFunction: (callback: FrameRequestCallback) => number;
    private _vrDevice: { ready: boolean; display: VRDisplay; isReady: () => any; presenting: boolean };
    private cwScrolling: boolean;
    private cwGetPageOffsets: (el: any) => { x: number; y: number };
    private cwGetScrollOffsets: () => { x: any; y: any };
    private cwEnableZoom: boolean = true;
    private cwScrollTimer: number = null;

    constructor(aSceneAPI: Scene, aGlCanvasParentTag: HTMLDivElement) {
        this._sceneAPI = aSceneAPI;
        this._glCanvasParentTag = aGlCanvasParentTag;

        this._contentSize = vec2.fromValues(0, 0);

        this.prevSquareDistance = 0;

        this._minimalUIVisible = false;

        this.animatedObjects = [];

        this._animationFrameRequestID = null;
        this._animationFrameContextObject = window;
        this._newRequestedAnimatioFrameOnObject = null;

        // Mozilla Touches
        this.mozillaIds = [];
        this.mozillaTouches = {
            touches: [],
            preventDefault: () => {
                //
            }
        };

        this._vrDeviceChecker = {
            checkRequested: true,
            displaysCount: 0
        };

        // click event helpers - needed to differ click from double click (do not trigger click action when double click happens)
        this._dblClickTimer = null;
        this._clickHandled = true;

        // Touch events
        this._touchLastTapStart = 0;
        this._touchLastTapEnd = 0;
        this._touchDoubleTapTimeout = null;
        this._touchLongTapTimeout = null;

        this._motionData = {
            deviceOrientation: {
                alpha: 0,
                beta: 0,
                gamma: 0
            },
            screenOrientation: 0,
            initialRoll: null,
            initialScreenOffset: null,
            changed: false
        };

        this._createHTMLCanvas();
        this._createHTML2DOverlayCanvas();

        // create the webgl context for the canvas
        this._webglContext = new WebGLContext(this, true);
        if (!this._webglContext.isValid()) {
            // here we have to emit an event which tells the listeners that the webgl is not supported because no valid context was found
            Messenger.fireCDWKEvent(this._glCanvasParentTag, "cwx3d_webglIsNotSupported", {});
            return;
        }

        let cwErrorMessages = [];
        let cwWebglFeaturesStatus = WebGLContext.checkRequiredOpenGLFeatures(cwErrorMessages);
        if (!cwWebglFeaturesStatus) {
            Messenger.fireCDWKEvent(this._glCanvasParentTag, "cwx3d_webglFeaturesMissing", {missingFeatures: cwErrorMessages});
            this._webglContext = null;
            return;
        }

        // events processing test; please keep it here; tb
        // cadwork.webgl.Messenger.fireCDWKEvent(x3dElem, "cwx3d_eventsProcessingTest", {text: 'udalost zpracovana'});

        let cwWarningMessages = [];
        let cwWebglOptFeaturesStatus = WebGLContext.checkOptionalOpenGLFeatures(cwWarningMessages);
        if (!cwWebglOptFeaturesStatus) {
            Messenger.fireCDWKEvent(
                this._glCanvasParentTag,
                "cwx3d_webglOptionalFeaturesMissing",
                {
                    missingOptionalFeatures: cwWarningMessages
                }
            );
        }

        this._canvas2DContext = new Canvas2DContext(this);

        // must be called when the gl context is already created
        this.initVRDevice();

        // start the main loop
        this._mainAnimationLoop = () => {

            // first request animation frame, then render content
            this._animationFrameContextObject.requestAnimationFrame(this._mainAnimationLoop);

            this .handleResize();
            this.tick();

            Messenger.processEvents(); // we control the events here as we needed to be sure that we
            // are not in the middle of rendering (traversing children) because
            // as a reaction to some events, the application can change the scene graph
            // because the dispatch event is synchronous and is therefore called immediately

            this._newRequestedAnimatioFrameOnObject = this._animationFrameContextObject;
        };

        // start the animation loop
        this._startAnimationLoop();

        this._pgc = {
            cssDim: vec2.create(),
            pysicalDim: vec2.create()
        };
    }

    public release() {
        // clear the mainloop
        this._stopAnimationLoop();

        // stop the anim frame
        Messenger.release();

        if (this._webglContext) {
            this._webglContext.release();
        }

        if (this._canvas2DContext) {
            this._canvas2DContext.release();
        }
    }

    /**
     * @return {cadwork.webgl.WebGLContext}
     */
    public getWebGLContext(): WebGLContext {
        return this._webglContext;
    }

    /**
     * @returns {cadwork.webgl.Canvas2DContext}
     */
    public getCanvas2DContext(): Canvas2DContext {
        return this._canvas2DContext;
    }

    /**
     * @return {boolean}
     */
    public hasValidContext(): boolean {
        return this._webglContext.isValid() && this._canvas2DContext.isValid();
    }

    public getSceneAPI(): Scene {
        return this._sceneAPI;
    }

    /**
     * @return {HTMLCanvasElement}
     */
    public getHTMLCanvas(): HTMLCanvasElement {
        return this.htmlCanvas;
    }

    public getHTML2DOverlayCanvas(): HTMLCanvasElement {
        return this.html2DOverlayCanvas;
    }

    public focus() {
        this.htmlCanvas.focus();
    }

    public addAnimatedObject(aAnimatedObject: AnimatedObject, aAnimator: CameraAnimator) {
        aAnimator.attachObject(aAnimatedObject);
        aAnimatedObject.attachAnimator(aAnimator);

        let index = this.animatedObjects.indexOf(aAnimatedObject);
        if (index === -1) {
            this.animatedObjects.push(aAnimatedObject);
        }
    }

    /*public removeAnimatedObject(aAnimatedObject: AnimatedObject) {
        let animatedObjectUID = aAnimatedObject.getAUID();
        let animation = this.animatedObjects[animatedObjectUID];
        if (animation !== undefined) {
            delete this.animatedObjects[animatedObjectUID];
            return true;
        }

        return false;
    }*/

    public removeAnimator(aAnimator: CameraAnimator) {
        for (let i = 0; i < this.animatedObjects.length; i++) {
            let animatedObject = this.animatedObjects[i];
            if (animatedObject.containsAnimator(aAnimator)) {
                animatedObject.removeAnimator(aAnimator);
                if (animatedObject.isEmpty()) {
                    this._removeEmptyAnimatedObject(i);
                    break;
                }
            }
        }
    }

    /**
     * Get mouse position with applied ratio between clientWidth, clientHeight and _contentSize
     * @param aView
     * @param evt
     * @returns {*}
     */
    public getPhysMousePosition(evt: MouseEvent) {
        let pos = this.getPageMousePosition(evt);

        return vec2.fromValues(
            pos[0] * this._contentSize[0] / this.htmlCanvas.clientWidth,
            pos[1] * this._contentSize[1] / this.htmlCanvas.clientHeight
        );
    }

    public getPhysMousePositionFromPagePosition(aPagePosition: number[]) {
        return vec2.fromValues(
            aPagePosition[0] * this._contentSize[0] / this.htmlCanvas.clientWidth,
            aPagePosition[1] * this._contentSize[1] / this.htmlCanvas.clientHeight
        );
    }

    public getPageMousePositionFromPhysPosition(aPhysPosition: number[]) {
        return vec2.fromValues(
            aPhysPosition[0] * this.htmlCanvas.clientWidth / this._contentSize[0],
            aPhysPosition[1] * this.htmlCanvas.clientHeight / this._contentSize[1]
        );
    }

    public getPageMousePosition(evt: MouseEvent) {
        let x = 0;
        let y = 0;

        if ("getBoundingClientRect" in document.documentElement) {
            // @ts-ignore
            let elem = evt.target.offsetParent;    // should be x3dElem
            let box = elem.getBoundingClientRect();

            let scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
            let scrollTop = window.pageYOffset || document.documentElement.scrollTop;

            let compStyle = document.defaultView.getComputedStyle(elem, null);

            let paddingLeft = parseFloat(compStyle.getPropertyValue('padding-left'));
            let borderLeftWidth = parseFloat(compStyle.getPropertyValue('border-left-width'));

            let paddingTop = parseFloat(compStyle.getPropertyValue('padding-top'));
            let borderTopWidth = parseFloat(compStyle.getPropertyValue('border-top-width'));

            x = Math.round(evt.pageX - (box.left + paddingLeft + borderLeftWidth + scrollLeft));
            y = Math.round(evt.pageY - (box.top + paddingTop + borderTopWidth + scrollTop));
        } else {
            Debug.logError('NO getBoundingClientRect');
        }

        return vec2.fromValues(x, y);
    }

    public initMouseEvents() {
        // Mouse Events
        let sceneAPI = this._sceneAPI;

        // cdwk: added mouse event forwarder to pass mouse position to webgl canvas
        // when mouse is not directly above the webgl canvas
        this.htmlCanvasExt.cdwkEventForwarder = (aEvent: MouseEvent) => {

            let x = aEvent.clientX;
            let y = aEvent.clientY;
            let event = document.createEvent('MouseEvents');

            let btn = 0;
            switch (this.htmlCanvasExt.mouseButton) {
                case 1:
                    btn = 0;
                    break;  // left
                case 4:
                    btn = 4;
                    break;  // middle
                case 2:
                    btn = 2;
                    break;  // right
                default:
                    btn = 0;
                    break;
            }

            event.initMouseEvent(aEvent.type, false, true, window,
                0, x, y, x, y, aEvent.ctrlKey, aEvent.altKey, aEvent.shiftKey, aEvent.metaKey, btn, null);

            this.htmlCanvas.dispatchEvent(event);
        };

        this.htmlCanvas.addEventListener('mousedown', (evt: MouseEvent) => {
            this.focus();
            this.htmlCanvas.classList.add('cwEngine-canvas-mousedown');

            switch (evt.button) {
                case 0:
                    this.htmlCanvasExt.mouseButton = 1;
                    break;  // left
                case 1:
                    this.htmlCanvasExt.mouseButton = 4;
                    break;  // middle
                case 2:
                    this.htmlCanvasExt.mouseButton = 2;
                    break;  // right
                default:
                    this.htmlCanvasExt.mouseButton = 0;
                    break;
            }

            if (evt.shiftKey) {
                this.htmlCanvasExt.mouseButton = 1;
            }
            if (evt.ctrlKey) {
                this.htmlCanvasExt.mouseButton = 4;
            }
            if (evt.altKey) {
                this.htmlCanvasExt.mouseButton = 2;
            }

            let pos = this.getPhysMousePosition(evt);
            this.htmlCanvasExt.dragX = pos[0];
            this.htmlCanvasExt.dragY = pos[1];
            this.htmlCanvasExt.downX = pos[0];
            this.htmlCanvasExt.downY = pos[1];

            this.htmlCanvasExt.dragging = true;

            // first check registered listeners on canvas because the view stop propagate the events because of performance reaaons
            this.getSceneAPI().invokeListener("mousedown", evt);

            if (!evt.cancelBubble) { // do not propagate when already cancelled
                sceneAPI.viewModeModule.onMousePress(this.htmlCanvasExt.dragX, this.htmlCanvasExt.dragY, this.htmlCanvasExt.mouseButton);
            }

            // Alternative capturing when mouse is above some page element and outside the webgl canvas
            this.htmlCanvasExt.cdwkMouseForwarding = true;
            window.addEventListener('mousemove', this.htmlCanvasExt.cdwkEventForwarder, false);
            window.addEventListener('mouseup', this.htmlCanvasExt.cdwkEventForwarder, false);
        }, false);

        this.htmlCanvas.addEventListener('mouseup', (evt: MouseEvent) => {
            let prevMouseButton = this.htmlCanvasExt.mouseButton;
            this.htmlCanvas.classList.remove('cwEngine-canvas-mousedown');
            this.htmlCanvasExt.mouseButton = 0;
            this.htmlCanvasExt.dragging = false;

            // first check registered listeners on canvas because the view stop propagate the events because of performance reaaons
            this._sceneAPI.invokeListener("mouseup", evt);

            if (!evt.cancelBubble) { // do not propagate when already cancelled
                sceneAPI.viewModeModule.onMouseRelease(this.htmlCanvasExt.dragX, this.htmlCanvasExt.dragY, this.htmlCanvasExt.mouseButton, prevMouseButton);
            }

            // Release alternative capturing when mouse is above some page element and outside the webgl canvas
            this.htmlCanvasExt.cdwkMouseForwarding = false;
            window.removeEventListener('mousemove', this.htmlCanvasExt.cdwkEventForwarder, false);
            window.removeEventListener('mouseup', this.htmlCanvasExt.cdwkEventForwarder, false);
        }, false);

        this.htmlCanvas.addEventListener('mouseover', (evt: MouseEvent) => {
            if (!this.htmlCanvasExt.cdwkMouseForwarding) {

                this.htmlCanvasExt.mouseButton = 0;
                this.htmlCanvasExt.dragging = false;

                // first check registered listeners on canvas because the view stop propagate the events because of performance reaaons
                this._sceneAPI.invokeListener("mouseover", evt);

                if (!evt.cancelBubble) { // do not propagate when already cancelled
                    sceneAPI.viewModeModule.onMouseIn(this.htmlCanvasExt.dragX, this.htmlCanvasExt.dragY, this.htmlCanvasExt.mouseButton);
                }
            }
        }, false);

        this.htmlCanvas.addEventListener('mouseout', (evt: MouseEvent) => {
            if (!this.htmlCanvasExt.cdwkMouseForwarding) {

                this.htmlCanvasExt.mouseButton = 0;
                this.htmlCanvasExt.dragging = false;

                // first check registered listeners on canvas because the view stop propagate the events because of performance reaaons
                this._sceneAPI.invokeListener("mouseout", evt);

                if (!evt.cancelBubble) { // do not propagate when already cancelled
                    sceneAPI.viewModeModule.onMouseOut(this.htmlCanvasExt.dragX, this.htmlCanvasExt.dragY, this.htmlCanvasExt.mouseButton);
                }
            }
        }, false);

        this.htmlCanvas.addEventListener('contextmenu', (evt: MouseEvent) => {
            let pos = this.getPhysMousePosition(evt);
            this.htmlCanvasExt.dragX = pos[0];
            this.htmlCanvasExt.dragY = pos[1];
            this.htmlCanvasExt.dragging = false;
            let draggedDuringClick = (this.htmlCanvasExt.dragX !== this.htmlCanvasExt.downX || this.htmlCanvasExt.dragY !== this.htmlCanvasExt.downY);

            // first check registered listeners on canvas because the view stop propagate the events because of performance reaaons
            if (!draggedDuringClick) {
                this._sceneAPI.invokeListener("contextmenu", evt);
            }

            // if (!evt.cancelBubble) { // do not propagate when already cancelled
            //     sceneAPI.viewModeModule.onClick(this.dragX, this.dragY, this.mouseButton, draggedDuringClick);
            // }
        }, false);

        this.htmlCanvas.addEventListener('click', (evt: MouseEvent) => {
            switch (evt.button) {
                case 0:
                    this.htmlCanvasExt.mouseButton = 1;
                    break;  // left
                case 1:
                    this.htmlCanvasExt.mouseButton = 4;
                    break;  // middle
                case 2:
                    this.htmlCanvasExt.mouseButton = 2;
                    break;  // right
                default:
                    this.htmlCanvasExt.mouseButton = 0;
                    break;
            }

            let pos = this.getPhysMousePosition(evt);
            this.htmlCanvasExt.dragX = pos[0];
            this.htmlCanvasExt.dragY = pos[1];
            this.htmlCanvasExt.dragging = false;
            let draggedDuringClick = (this.htmlCanvasExt.dragX !== this.htmlCanvasExt.downX || this.htmlCanvasExt.dragY !== this.htmlCanvasExt.downY);

            let thisEvent = this.htmlCanvasExt;
            this._clickHandled = false;

            if (!evt.shiftKey) { // direct handling of click event when no key is down
                this._clickHandled = true;
                this._sceneAPI.invokeListener("click", evt);
                if (!evt.cancelBubble) { // do not propagate when already cancelled
                    sceneAPI.viewModeModule.onClick(thisEvent.dragX, thisEvent.dragY, thisEvent.mouseButton, draggedDuringClick);
                }
            }
            else { // when shift key is down, make difference between single click and double click
                // do zoom to point when shift down
                this._dblClickTimer = window.setTimeout(() => {

                    if (!this._clickHandled) {
                        this._clickHandled = true;

                        // first check registered listeners on canvas because the view stop propagate the events because of performance reaaons
                        this._sceneAPI.invokeListener("click", evt);

                        if (!evt.cancelBubble) { // do not propagate when already cancelled
                            sceneAPI.viewModeModule.onClick(thisEvent.dragX, thisEvent.dragY, thisEvent.mouseButton, draggedDuringClick);
                        }
                    }

                }, Canvas.SingleDoubleClickLimit);
            }
        }, false);

        this.htmlCanvas.addEventListener('dblclick', (evt: MouseEvent) => {
            this._clickHandled = true;
            this.htmlCanvasExt.mouseButton = 0;

            let pos = this.getPhysMousePosition(evt);
            this.htmlCanvasExt.dragX = pos[0];
            this.htmlCanvasExt.dragY = pos[1];
            this.htmlCanvasExt.dragging = false;

            // first check registered listeners on canvas because the view stop propagate the events because of performance reasons
            this._sceneAPI.invokeListener("dblclick", evt);

            if (!evt.cancelBubble) { // do not propagate when already cancelled
                sceneAPI.viewModeModule.onDoubleClick(this.htmlCanvasExt.dragX, this.htmlCanvasExt.dragY);
            }
        }, false);

        this.htmlCanvas.addEventListener('mousemove', (evt: MouseEvent) => {
            if (evt.shiftKey) {
                this.htmlCanvasExt.mouseButton = 1;
            }
            if (evt.ctrlKey) {
                this.htmlCanvasExt.mouseButton = 4;
            }
            if (evt.altKey) {
                this.htmlCanvasExt.mouseButton = 2;
            }

            let pos = this.getPhysMousePosition(evt);
            // Debug.logInfo('mm dragging: ' + this.dragging +' '+ evt.button + " " + pos[0] + ', ' + pos[1] + ' mBtn=' + this.mouseButton);
            this.htmlCanvasExt.dragX = pos[0];
            this.htmlCanvasExt.dragY = pos[1];

            // first check registered listeners on canvas because the view stop propagate the events because of performance reaaons
            this._sceneAPI.invokeListener("mousemove", evt);

            if (!evt.cancelBubble) { // do not propagate when already cancelled
                if (this.htmlCanvasExt.dragging) {
                    sceneAPI.viewModeModule.onDrag(this.htmlCanvasExt.dragX, this.htmlCanvasExt.dragY, 0, this.htmlCanvasExt.mouseButton);
                } else {
                    sceneAPI.viewModeModule.onMove(this.htmlCanvasExt.dragX, this.htmlCanvasExt.dragY, this.htmlCanvasExt.mouseButton);
                }
            }

            // deliberately different for performance reasons
            evt.preventDefault();
            evt.stopPropagation();
        }, false);

        this.htmlCanvas.addEventListener('DOMMouseScroll', (evt: MouseEvent) => {
            this.focus();

            // first check registered listeners on canvas because the view stop propagate the events because of performance reaaons
            this._sceneAPI.invokeListener("DOMMouseScroll", evt);

            let pos = this.getPhysMousePosition(evt);
            this.htmlCanvasExt.dragX = pos[0];
            this.htmlCanvasExt.dragY = pos[1];

            if (!evt.cancelBubble) { // do not propagate when already cancelled
                sceneAPI.viewModeModule.onWheel(this.htmlCanvasExt.dragX, this.htmlCanvasExt.dragY, -4 * evt.detail, 8);
            }

            evt.preventDefault();
            evt.stopPropagation();
        }, false);

        // cdwk hacky? solution to not to zoom in canvas when scrolling (on mouse wheel) whole page
        // When the scroll starts above x3d tag content zoom is correctly performed
        this.cwScrolling = false;
        this.cwGetPageOffsets = (el: any) => {
            let sOff = this.cwGetScrollOffsets();
            let left = 0;
            let top = 0;
            let props;

            if (el.getBoundingClientRect) {
                props = el.getBoundingClientRect();
                left = props.left + sOff.x;
                top = props.top + sOff.y;
            } else { // for older browsers
                let elem;
                do {
                    left += el.offsetLeft;
                    top += el.offsetTop;
                    elem = el.offsetParent;
                } while (elem);
            }
            return {
                x: Math.round(left),
                y: Math.round(top)
            };
        };

        this.cwGetScrollOffsets = () => {
            let doc = document;
            let w = window;
            let x;
            let y;
            let docEl;

            if (typeof w.pageYOffset === 'number') {
                x = w.pageXOffset;
                y = w.pageYOffset;
            } else {
                if (doc.compatMode && doc.compatMode === 'CSS1Compat') {
                    docEl = doc.documentElement;
                } else {
                    docEl = doc.body;
                }

                x = docEl.scrollLeft;
                y = docEl.scrollTop;
            }

            return {
                x,
                y
            };
        };

        // global mousewheel event handler
        // detect if scrolling starts above one of x3d tag or not
        document.addEventListener('mousewheel', (evt: MouseEvent) => {
            this.cwEnableZoom = false;

            // first check registered listeners on canvas because the view stop propagate the events because of performance reaaons
            this._sceneAPI.invokeListener("mousewheel", evt);

            if (!this.cwScrolling) {
                this.cwScrolling = true;

                let x;
                let y;
                let sOff = this.cwGetScrollOffsets();

                x = Math.round(evt.clientX + sOff.x);
                y = Math.round(evt.clientY + sOff.y);

                // Debug.logInfo('doc.mwheel scroll start at: ' + x + ", " + y);

                let startAboveCadwork = false;
                let cwElements: any = document.getElementsByTagName("x3d");

                // TODO: we do nto have any x3d tag anymore
                for (let cwElement of cwElements) {

                    let elementOff = this.cwGetPageOffsets(cwElement);
                    let elementBottomOff = {
                        x: elementOff.x + cwElement.offsetWidth,
                        y: elementOff.y + cwElement.offsetHeight
                    };

                    if ((x >= elementOff.x && x <= elementBottomOff.x) && (y >= elementOff.y && y <= elementBottomOff.y)) {
                        // Debug.logInfo('ABOVE: ' + i);
                        startAboveCadwork = true;
                    }
                }

                if (startAboveCadwork) { // inside cw element
                    this.cwEnableZoom = true;
                    // Debug.logInfo('enable true');
                } else {
                    this.cwEnableZoom = false; // outside a page
                }
            }

            // consider scrolling is over after defined interval
            clearInterval(this.cwScrollTimer);
            this.cwScrollTimer = window.setInterval(() => {
                // Debug.logInfo("NOT SCROLLING");
                this.cwScrolling = false;
                window.clearInterval(this.cwScrollTimer);
            }, 500); // set an ms interval when pause in scrolling is considered as a start of a new scroll

        }, false);

        // cdwk: mouse wheel issue
        this.htmlCanvas.addEventListener('mousewheel', (evt: any) => {

            // first check registered listeners on canvas because the view stop propagate the events because of performance reaaons
            this._sceneAPI.invokeListener("mousewheel", evt);

            if (this.cwScrolling && !this.cwEnableZoom) {
                return;
            }

            this.focus();

            let pos = this.getPhysMousePosition(evt);
            this.htmlCanvasExt.dragX = pos[0];
            this.htmlCanvasExt.dragY = pos[1];

            // in IE11 the evt.wheelDeltaY is named evt.wheelDelta
            if (!evt.wheelDeltaY) {
                if (!evt.wheelDelta) {
                    if (evt.deltaY) {
                        this.htmlCanvasExt.cdwkMouseWheelDelta = Math.abs(evt.deltaY) > 50 ? 0.1 * evt.deltaY : evt.deltaY;
                    }
                    else {
                        this.htmlCanvasExt.cdwkMouseWheelDelta = 0;
                    }
                }
                else {
                    this.htmlCanvasExt.cdwkMouseWheelDelta = 0.1 * evt.wheelDelta;
                }
            }
            else {
                this.htmlCanvasExt.cdwkMouseWheelDelta = 0.1 * evt.wheelDeltaY;
            }

            if (!evt.cancelBubble) { // do not propagate when already cancelled
                this._sceneAPI.viewModeModule.onWheel(this.htmlCanvasExt.dragX, this.htmlCanvasExt.dragY, this.htmlCanvasExt.cdwkMouseWheelDelta, 8);
            }

            evt.preventDefault();
            evt.stopPropagation();
        }, false);

        window.onfocus = () => {
            if (this._sceneAPI.viewModeModule) {
                this._sceneAPI.viewModeModule.onFocus(null);
            }
        };
    }

    /**
     * Watches for a resize of the canvas and sets the current dimensions
     */
    public handleResize() {
        if (!this._sceneAPI.renderSettingsModule) {
            return;
        }

        if (this._vrDevice.display && this._vrDevice.display.isPresenting) {
            // If we're presenting we want to use the drawing buffer size
            // recommended by the VRDevice, since that will ensure the best
            // results post-distortion.
            let left = this._vrDevice.display.getEyeParameters('left');
            let right = this._vrDevice.display.getEyeParameters('right');

            // For simplicity we're going to render both eyes at the same size,
            // even if one eye needs less resolution. You can render each eye at
            // the exact size it needs, but you'll need to adjust the viewports to
            // account for that.
            this.setSize(Math.max(left.renderWidth, right.renderWidth) * 2, Math.max(left.renderHeight, right.renderHeight));
        } else {
            let devicePixelRatio = this._sceneAPI.renderSettingsModule.getQualityPerformanceFactor();

            // this display size of the canvas determined by the browser
            vec2.set(this._pgc.cssDim, this.htmlCanvas.clientWidth, this.htmlCanvas.clientHeight);

            // the device pixel ratio is necessary to get physical required pixels for e.g. iDevices
            // http://www.paintcodeapp.com/news/ultimate-guide-to-iphone-resolutions
            vec2.scale(this._pgc.pysicalDim, this._pgc.cssDim, devicePixelRatio);

            this.setSize(this._pgc.pysicalDim[0], this._pgc.pysicalDim[1]);

            this._watchForMinimalUIIsVisible();
        }
    }

    public setWebVRRequestAnimationFrameEnabled(aValue) {
        if (aValue && this._vrDevice.display.isPresenting) {
            this._animationFrameContextObject = this.getVRDevice().display;
            this._animationFrameFunction = this.getVRDevice().display.requestAnimationFrame;
        } else {
            this._animationFrameContextObject = window;
            this._animationFrameFunction = window.requestAnimationFrame;
        }
    }

    public isWebVRRequestAnimationFrameScheduled() {
        return (this._newRequestedAnimatioFrameOnObject !== window);
    }

    public setSize(aWidth, aHeight) {
        if (!this._sceneAPI.renderSettingsModule) {
            return;
        }

        if ((this._contentSize[0] !== aWidth) || (this._contentSize[1] !== aHeight)) {
            vec2.set(this._contentSize, aWidth, aHeight);
            this.htmlCanvas.setAttribute("width", aWidth + "px");
            this.htmlCanvas.setAttribute("height", aHeight + "px");

            this.html2DOverlayCanvas.setAttribute("width", aWidth + "px");
            this.html2DOverlayCanvas.setAttribute("height", aHeight + "px");

            // Debug.logInfo("set canvas size: " + aWidth + ", " + aHeight);

            if (this._sceneAPI.viewModeModule) {
                // do not allow rendering in case that one or both of the dimensions have zero size
                if (aWidth !== 0 && aHeight !== 0) {
                    this._sceneAPI.viewModeModule.scheduleResize(this._contentSize);
                    this._sceneAPI.viewModeModule.scheduleRedraw();
                }
            }
        }
    }

    public initKeyEvents() {
        // Key Events
        this.htmlCanvas.addEventListener('keypress', (evt: KeyboardEvent) => {
            this._sceneAPI.viewModeModule.onKeyPress(evt.key, this.htmlCanvasExt.dragX, this.htmlCanvasExt.dragY);
        }, true);

        // in webkit special keys are only handled on key-up
        this.htmlCanvas.addEventListener('keyup', (evt) => {
            this._sceneAPI.viewModeModule.onKeyUp(evt.key);
        }, true);

        this.htmlCanvas.addEventListener('keydown', (evt) => {
            this._sceneAPI.viewModeModule.onKeyDown(evt.key);
        }, true);
    }

    public initTouchEvents() {
        let touchStartHandler = (evt) => {
            evt.preventDefault();
            this._touchLastTapStart = new Date().getTime();

            this.focus();

            if (evt.touches.length === 1) {
                let pos = this.getPhysMousePosition(evt.touches[0]);

                this.htmlCanvasExt.dragX = pos[0];
                this.htmlCanvasExt.dragY = pos[1];
                this.htmlCanvasExt.downX = pos[0];
                this.htmlCanvasExt.downY = pos[1];

                this.htmlCanvasExt.dragging = false;

                this._sceneAPI.viewModeModule.onTouchStart(pos[0], pos[1], evt.touches.length);
            }
            else if (evt.touches.length === 2) {

                // in case we have two touch points, just take the middle point and use it as a dragged/zoom in/out point
                let touch0 = this.getPhysMousePosition(evt.touches[0]);
                let touch1 = this.getPhysMousePosition(evt.touches[1]);

                // distance vector
                let distance = vec2.create();
                vec2.subtract(distance, touch1, touch0);

                // middle
                let middle = vec2.create();
                vec2.scale(distance, distance, 0.5);
                vec2.add(middle, touch0, distance);

                this.htmlCanvasExt.dragX = middle[0];
                this.htmlCanvasExt.dragY = middle[1];
                this.htmlCanvasExt.downX = middle[0];
                this.htmlCanvasExt.downY = middle[1];

                this.htmlCanvasExt.dragging = true;

                this._sceneAPI.viewModeModule.onTouchStart(middle[0], middle[1], evt.touches.length);
            }

            Messenger.fireCDWKEvent(this._sceneAPI.getTargetTag(), "cwx3d_sceneTouchManipulation", {});
        };

        let touchMoveHandler = (evt) => {
            evt.preventDefault();

            if (evt.touches.length === 1) {
                let pos = this.getPhysMousePosition(evt.touches[0]);

                this.htmlCanvasExt.dragX = pos[0];
                this.htmlCanvasExt.dragY = pos[1];

                if (Math.abs(this.htmlCanvasExt.dragX - this.htmlCanvasExt.downX) > Canvas.SingleTapRadius &&
                    Math.abs(this.htmlCanvasExt.dragY - this.htmlCanvasExt.downY) > Canvas.SingleTapRadius) {
                    this.htmlCanvasExt.dragging = true;

                    this._sceneAPI.viewModeModule.onTouchMove(pos[0], pos[1], evt.touches.length, 0);
                }

            }
            else if (evt.touches.length === 2) {
                let touch0 = this.getPhysMousePosition(evt.touches[0]);
                let touch1 = this.getPhysMousePosition(evt.touches[1]);

                // distance vector
                let distance = vec2.create();
                vec2.subtract(distance, touch1, touch0);

                // middle
                let middle = vec2.create();
                vec2.scale(distance, distance, 0.5);
                vec2.add(middle, touch0, distance);

                let squareDistance = vec2.dot(distance, distance);

                let deltaZoom = this.prevSquareDistance === 0 ? 0 : squareDistance - this.prevSquareDistance;
                let deltaZoomIndependentFromResolution = deltaZoom / Math.max(this._contentSize[0], this._contentSize[1]);

                this.htmlCanvasExt.dragX = middle[0];
                this.htmlCanvasExt.dragY = middle[1];
                this.htmlCanvasExt.dragging = true;

                this._sceneAPI.viewModeModule.onTouchMove(middle[0], middle[1], evt.touches.length, deltaZoomIndependentFromResolution);

                this.prevSquareDistance = squareDistance;
            }
        };

        let touchEndHandler = (evt) => {
            evt.preventDefault();

            // the two fingers gesture finished
            if (evt.changedTouches.length === 1 && evt.touches.length === 1) {

                let touch0 = this.getPhysMousePosition(evt.changedTouches[0]);
                let touch1 = this.getPhysMousePosition(evt.touches[0]);

                // distance vector
                let distance = vec2.create();
                vec2.subtract(distance, touch1, touch0);

                // middle
                let middle = vec2.create();
                vec2.scale(distance, distance, 0.5);
                vec2.add(middle, touch0, distance);

                this.htmlCanvasExt.dragX = middle[0];
                this.htmlCanvasExt.dragY = middle[1];
                this.htmlCanvasExt.upX = touch0[0];
                this.htmlCanvasExt.upY = touch0[1];
                this.htmlCanvasExt.dragging = false;

                this._sceneAPI.viewModeModule.onTouchEnd(middle[0], middle[1], touch1);
                this._sceneAPI.viewModeModule.onTouchStart(touch1[0], touch1[1], evt.touches.length);
            }
            else if (evt.changedTouches.length === 1 && evt.touches.length === 0) { // the last touch finished
                let currentTime = new Date().getTime();

                let pos = this.getPhysMousePosition(evt.changedTouches[0]);
                // console.log('pos ' + pos[0]+ ',  ' + pos[1]);

                // let prevUpX = this.upX;
                // let prevUpY = this.upY;

                this.htmlCanvasExt.upX = pos[0];
                this.htmlCanvasExt.upY = pos[1];
                this.htmlCanvasExt.downX = pos[0];
                this.htmlCanvasExt.downY = pos[1];

                let tapDuration = currentTime - this._touchLastTapStart;
                // console.log('tap duration ' + tapDuration + ' drag: ' + this.dragging);
                if (!this.htmlCanvasExt.dragging) {

                    if (tapDuration < Canvas.LongTapLimit) {
                        // console.log('single tap');
                        if (Math.abs(this.htmlCanvasExt.dragX - this.htmlCanvasExt.downX) < Canvas.SingleTapRadius &&
                            Math.abs(this.htmlCanvasExt.dragY - this.htmlCanvasExt.downY) < Canvas.SingleTapRadius) {
                            this._sceneAPI.viewModeModule.onTouchEnd(this.htmlCanvasExt.dragX, this.htmlCanvasExt.dragY, null);
                            this._sceneAPI.viewModeModule.onSingleTap(this.htmlCanvasExt.downX, this.htmlCanvasExt.downY);
                        }
                    }
                    else {
                        // console.log('long tap');
                        // console.log('evt type ' + evt.type + ' ' );
                        if (Math.abs(this.htmlCanvasExt.dragX - this.htmlCanvasExt.downX) < Canvas.SingleTapRadius &&
                            Math.abs(this.htmlCanvasExt.dragY - this.htmlCanvasExt.downY) < Canvas.SingleTapRadius) {
                            this._sceneAPI.invokeListener("contextmenu", evt);
                        }
                    }
                }
                this.htmlCanvasExt.dragX = pos[0];
                this.htmlCanvasExt.dragY = pos[1];
                this.htmlCanvasExt.dragging = false;

                // this is for the special mode when measuring on touch devices so taht weh we move with finger,
                // then on touch finishes we emit a touch end ewen even the touch end point differs from the touch start
                if (this._sceneAPI.pickingModule.isCameraRotationDisabled()) {
                     this._sceneAPI.viewModeModule.onTouchEnd(this.htmlCanvasExt.dragX, this.htmlCanvasExt.dragY, null);
                }
                this._touchLastTapEnd = currentTime;
            }
            this.prevSquareDistance = 0;
        };

        let touchCancelHandler = (evt) => {
            evt.preventDefault();
            Debug.logInfo('TB: touchCancelHandler called!!!');
        };

        // mozilla touch events
        this.htmlCanvas.addEventListener('MozTouchDown', (evt: any) => {
            evt.preventDefault();

            let newId = true;
            for (let id of this.mozillaIds) {
                if (id === evt.streamId) {
                    newId = false;
                }
            }

            if (newId === true) {
                evt.identifier = evt.streamId;
                this.mozillaIds.push(evt.streamId);
                this.mozillaTouches.touches.push(evt);
            }
            touchStartHandler(this.mozillaTouches);
        }, true);

        this.htmlCanvas.addEventListener('MozTouchMove', (evt: any) => {
            evt.preventDefault();

            for (let i = 0; i < this.mozillaIds.length; ++i) {
                if (this.mozillaIds[i] === evt.streamId) {
                    this.mozillaTouches.touches[i] = evt;
                }
            }

            touchMoveHandler(this.mozillaTouches);
        }, true);

        this.htmlCanvas.addEventListener('MozTouchUp', (evt: any) => {
            evt.preventDefault();

            let removeIndex = -1;
            for (let i = 0; i < this.mozillaIds.length; ++i) {
                if (this.mozillaIds[i] === evt.streamId) {
                    removeIndex = i;
                }
            }

            if (removeIndex !== -1) {
                this.mozillaIds.splice(removeIndex, 1);
                this.mozillaTouches.touches.splice(removeIndex, 1);
            }

            touchEndHandler(this.mozillaTouches);
        }, true);

        // w3c / apple touch events (in Chrome via chrome://flags)
        this.htmlCanvas.addEventListener('touchstart', touchStartHandler, true);
        this.htmlCanvas.addEventListener('touchmove', touchMoveHandler, true);
        this.htmlCanvas.addEventListener('touchend', touchEndHandler, true);
        this.htmlCanvas.addEventListener('touchcancel', touchCancelHandler, true);
    }

    public getMotionData() {
        return this._motionData;
    }

    public initMotionEvents() {
        // @ts-ignore
        if (!window.DeviceOrientationEvent) {
            Debug.logInfo("DeviceOrientation is not supported");
            return;
        }

        // Listen for the event and handle DeviceOrientationEvent object
        window.addEventListener('deviceorientation', (aEvent: DeviceOrientationEvent) => {
            if (!this._sceneAPI.viewControlsModule.getDeviceOrientationEventEnabled()) {
                return;
            }

            // evt.preventDefault();
            this._motionData.deviceOrientation.alpha = aEvent.alpha;
            this._motionData.deviceOrientation.beta = aEvent.beta;
            this._motionData.deviceOrientation.gamma = aEvent.gamma;

            if (this._motionData.initialRoll === null && aEvent.gamma) {
                this._motionData.initialRoll = aEvent.gamma;
            }

            this._motionData.changed = true;
            this._motionData.screenOrientation = (window.orientation || 0);
        }, true);

        window.addEventListener('orientationchange', (aEvent) => {
            /* jshint unused:false */
            this._motionData.screenOrientation = (window.orientation || 0);

            if (this._motionData.initialScreenOffset === null) {
                this._motionData.initialScreenOffset = this._motionData.screenOrientation;
            }

            this._motionData.changed = true;
        }, true);
    }

    /**
     * Todo: use connected and disconnected events defined within the webvr api (not supported in Firefox so wait for support for this feature).
     */
    public checkVRDevice() {

        if (this._vrDeviceChecker.checkRequested) {
            if (navigator.getVRDisplays === undefined) {
                this._vrDeviceChecker.checkRequested = false;
                // tell application that WebVR is not available
                Messenger.fireCDWKEvent(this._glCanvasParentTag, "cwx3d_webVRMissing", {});
                Debug.logInfo('Navigator interface has not getVRDisplays method defined.');
                return;
            }

            this._vrDeviceChecker.checkRequested = false;
            navigator.getVRDisplays().then(
                (aDisplays) => {
                    if (aDisplays.length !== this._vrDeviceChecker.displaysCount) {
                        this._vrDeviceChecker.displaysCount = aDisplays.length;

                        if (aDisplays.length > 0) {
                            // we take first one
                            this._vrDevice.display = aDisplays[aDisplays.length - 1];

                            if (this._vrDevice.display) {
                                Debug.logInfo("Display for VR device ready:");
                                Debug.logInfo("Begin VR display capabilities:");
                                for (let member in this._vrDevice.display.capabilities) {
                                    Debug.logInfo(member + ": " + this._vrDevice.display.capabilities[member]);
                                }
                                Debug.logInfo("End VR display capabilities.");
                                this._vrDevice.ready = true;
                                Messenger.fireCDWKEvent(
                                    this._glCanvasParentTag,
                                    "cwx3d_webVRReady", null
                                );
                            } else {
                                Messenger.fireCDWKEvent(this._glCanvasParentTag, "cwx3d_webVRNotReady", {});
                                Debug.logInfo("Display for VR device is not available.");
                            }
                        } else {
                            this._vrDevice.display = null;
                            this._vrDevice.ready = false;
                            // webVR available, but no VR devices attached
                            Messenger.fireCDWKEvent(this._glCanvasParentTag, "cwx3d_webVRNotReady", {});
                            Debug.logInfo('No displays found for VR device');
                        }
                    }
                }
            );

            // schedule next check
            setTimeout(() => {
                this._vrDeviceChecker.checkRequested = true;
            }, 2000);
        }
    }

    public initVRDevice() {
        this._vrDevice = {
            display: null,
            ready: false,
            presenting: false,
            isReady: () => {
                return this._vrDevice.ready;
            }
        };

        this.checkVRDevice();
    }

    public getVRDevice(): {display: VRDisplay} {
        return this._vrDevice;
    }

    public tick() {
        this.checkVRDevice();

        // if the canvas has zero dimensions (at least one) do not render nothing
        let canvasDim = vec2.fromValues(this.htmlCanvas.clientWidth, this.htmlCanvas.clientHeight);

        if (canvasDim[0] === 0 || canvasDim[1] === 0) {
            return;
        }

        if (this._sceneAPI.viewModule === undefined || !this._sceneAPI.viewModeModule) {
            return;
        }

        this.animationTick();

        this._sceneAPI.viewModeModule.tick();

        if (!this._sceneAPI.viewControlsModule.isMoving()) {
            this._sceneAPI.getGeoPathsManager().tick();
        }

        this._sceneAPI.getCanvasTextRenderer().tick();

        this._sceneAPI.printModule.tick();
        this._sceneAPI.viewModeModule.render();
        this._sceneAPI.printModule.postRenderTick();
    }

    /**
     * Returns contentSize reference
     * Be careful changing the reference causes infinite redraw loop.
     */
    public getContentSize() {
        return this._contentSize;
    }

    public animationTick() {
        for (let i = 0; i < this.animatedObjects.length; i++) {
            let animatedObject = this.animatedObjects[i];
            animatedObject.animationTick();
            if (animatedObject.isEmpty()) {
                this._removeEmptyAnimatedObject(i);
                break;
            }
        }
    }

    private _startAnimationLoop() {
        this._animationFrameRequestID = this._animationFrameContextObject.requestAnimationFrame(this._mainAnimationLoop);
    }

    private _stopAnimationLoop() {
        if (this._animationFrameRequestID) {
            this._animationFrameContextObject.cancelAnimationFrame(this._animationFrameRequestID);
        }

        this._animationFrameRequestID = null;
    }

    private _removeEmptyAnimatedObject(aIndex: number) {
        this.animatedObjects.splice(aIndex, 1);
    }

    /**
     * Creates the HTML canvas used as render target
     */
    private _createHTMLCanvas() {
        Debug.logInfo("Creating canvas for webgl element...");
        this.htmlCanvas = document.createElement('canvas');
        this.htmlCanvas.setAttribute("class", "cwEngine-canvas");
        this.htmlCanvas.setAttribute("height", "100%");

        this._glCanvasParentTag.appendChild(this.htmlCanvas);

        // http://snook.ca/archives/accessibility_and_usability/elements_focusable_with_tabindex
        this.htmlCanvas.setAttribute("tabIndex", "0");

        this._initCanvas();
    }

    /**
     * Creates the HTML canvas used as render target
     */
    private _createHTML2DOverlayCanvas() {
        Debug.logInfo("Creating canvas for 2d drawing over the webgl canvas...");
        this.html2DOverlayCanvas = document.createElement('canvas');
        this.html2DOverlayCanvas.setAttribute("class", "cwEngine-2dCanvas-overlay");
        this.html2DOverlayCanvas.setAttribute("height", "100%");

        let sceneOverlaysWrapper = this._sceneAPI.getOverlaysWrapper();
        sceneOverlaysWrapper.appendChild(this.html2DOverlayCanvas);

        // http://snook.ca/archives/accessibility_and_usability/elements_focusable_with_tabindex
        this.html2DOverlayCanvas.setAttribute("tabIndex", "0");
    }

    private _initCanvas() {

        this.htmlCanvasExt = new HtmlCanvasExt();

        this.htmlCanvas.oncontextmenu = (evt) =>  {
            evt.preventDefault();
            evt.stopPropagation();
            return false;
        };

        this.htmlCanvas.addEventListener("webglcontextlost", (event) => {
            Debug.logError("WebGL context lost");
            event.preventDefault();
        }, false);

        this.htmlCanvas.addEventListener("webglcontextrestored", (event) => {
            Debug.logError("recover WebGL state and resources on context lost NYI");
            event.preventDefault();
        }, false);

        this.initMouseEvents();

        this.initKeyEvents();

        this.initTouchEvents();

        this.initMotionEvents();
    }

    private _watchForMinimalUIIsVisible() {
        if (!this.htmlCanvas) {
            return;
        }

        let muivisible = this.htmlCanvas.clientHeight !== window.innerHeight;
        let muiStateChange = muivisible !== this._minimalUIVisible;
        if (muiStateChange) {
            Messenger.fireCDWKEvent(this._sceneAPI.getTargetTag(), "cwx3d_minimalUIVisibilityStateChanged", {});
        }

        this._minimalUIVisible = muivisible;
    }
}
